import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowLeftRight, Loader2 } from "lucide-react";

import { apiService, type Lesson } from "@/services/api.service";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { lessonDisplayName, swapIdError } from "./swap-lesson";

type LessonSummaryProps = { label: string; id: string; title: string; muted?: boolean };

// As the Alloy design draws it: the lesson going out is muted and struck
// through, the one coming in plain.
function LessonSummary({ label, id, title, muted }: LessonSummaryProps) {
  return (
    <div className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 ${muted ? "bg-muted" : "bg-background"}`}>
      <span className="w-[52px] shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <span className={`min-w-0 flex-1 truncate text-[13px] font-medium ${muted ? "text-muted-foreground line-through" : "text-foreground"}`} title={title}>{title}</span>
      <span className="shrink-0 text-xs text-muted-foreground">ID: {id}</span>
    </div>
  );
}

type SwapConfirmPanelProps = {
  current: { id: string; title: string };
  next: { id: string; title: string };
  onCancel: () => void;
  onConfirm: () => void;
};

/** The confirmation's content, apart from its dialog so it renders statically. */
export function SwapConfirmPanel({ current, next, onCancel, onConfirm }: SwapConfirmPanelProps) {
  return (
    <>
      <DialogHeader className="gap-1.5">
        <DialogTitle className="text-base font-semibold">Confirm lesson swap</DialogTitle>
        <DialogDescription className="text-[13px] leading-normal">
          The current lesson will be replaced by the new lesson in the same position.
        </DialogDescription>
      </DialogHeader>
      {/* min-w-0: DialogContent is a grid, and without it a long title's
          full width sets the column, pushing everything past the dialog. */}
      <div className="flex min-w-0 flex-col gap-2">
        <LessonSummary label="Current" id={current.id} title={current.title} muted />
        <div className="flex justify-center text-muted-foreground">
          <ArrowDown className="size-4" />
        </div>
        <LessonSummary label="New" id={next.id} title={next.title} />
      </div>
      <DialogFooter>
        <Button variant="outline" className="h-9 px-3.5 text-[13px]" onClick={onCancel}>Cancel</Button>
        <Button autoFocus className="h-9 px-3.5 text-[13px]" onClick={onConfirm}>Confirm swap</Button>
      </DialogFooter>
    </>
  );
}

type Props = {
  currentId: string;
  currentTitle: string;
  idsInUse: ReadonlySet<string>;
  disabled?: boolean;
  onSwap: (lesson: Lesson) => void;
};

/**
 * Replace the lesson in a slot with another, by ID. The slot keeps its place:
 * a swap only changes which lesson sits there (R35). Takes effect on Save.
 */
export function SwapLessonButton({ currentId, currentTitle, idsInUse, disabled, onSwap }: Props) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [pending, setPending] = useState<Lesson | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // A lookup still in flight when the popover closes must not reopen anything.
  const lookup = useRef(0);

  const close = () => {
    lookup.current += 1;
    setOpen(false);
    setValue("");
    setError(null);
    setChecking(false);
  };

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const submit = async () => {
    const problem = swapIdError(value, currentId, idsInUse);
    if (problem) {
      setError(problem);
      return;
    }
    const id = Number(value.trim());
    const request = ++lookup.current;
    setChecking(true);
    try {
      const lesson = await apiService.findLessonById(id);
      if (request !== lookup.current) return;
      if (!lesson) {
        setError(`No lesson with ID ${id}`);
        setChecking(false);
        return;
      }
      close();
      setPending({ ...lesson, name: lessonDisplayName(lesson) });
    } catch {
      if (request !== lookup.current) return;
      setError("Couldn't look up that lesson. Try again.");
      setChecking(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      <Button
        variant="ghost"
        size="icon"
        className={`size-8 ${open ? "bg-accent" : ""}`}
        title="Swap lesson"
        aria-label="Swap lesson"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        disabled={disabled}
      >
        <ArrowLeftRight className="size-4 text-muted-foreground" />
      </Button>
      {open ? (
        <div
          role="dialog"
          aria-label="Swap lesson"
          className="absolute top-[calc(100%+6px)] right-0 z-50 flex w-60 flex-col gap-2 rounded-lg border bg-background p-3 shadow-lg"
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-semibold text-foreground">Swap lesson</span>
            <span className="text-xs text-muted-foreground">Replace ID {currentId} with another lesson</span>
          </div>
          <Input
            ref={inputRef}
            aria-label="New lesson ID"
            inputMode="numeric"
            placeholder="New lesson ID"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                // Without this the same Enter, finishing after the lookup,
                // lands on the confirmation's first button -- Cancel.
                event.preventDefault();
                void submit();
              }
              if (event.key === "Escape") close();
            }}
            aria-invalid={error ? true : undefined}
            className="h-8"
          />
          {error ? <span role="alert" className="text-xs text-destructive">{error}</span> : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={close}>Cancel</Button>
            <Button size="sm" className="h-7 gap-1.5 px-2.5 text-xs" onClick={() => void submit()} disabled={checking}>
              {checking ? <Loader2 className="size-3 animate-spin" /> : null}
              Swap
            </Button>
          </div>
        </div>
      ) : null}
      <Dialog open={pending !== null} onOpenChange={(next) => !next && setPending(null)}>
        {pending ? (
          <DialogContent
            showCloseButton={false}
            className="gap-5 rounded-xl p-6 sm:max-w-[440px]"
            // The design focuses Confirm; Radix would otherwise focus Cancel.
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <SwapConfirmPanel
              current={{ id: currentId, title: currentTitle }}
              next={{ id: String(pending.lesson_id), title: pending.name }}
              onCancel={() => setPending(null)}
              onConfirm={() => {
                onSwap(pending);
                setPending(null);
              }}
            />
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
